/* Hallmark · component: Select · genre: modern-minimal · theme: maeiz-tokens
 * Custom Radix select (not native <select>) — collision-aware menu
 */
import * as SelectPrimitive from "@radix-ui/react-select";
import React from "react";
import { clsx } from "clsx";
import { Check, ChevronDown } from "lucide-react";
import { CONTROL_HEIGHT, fieldSurfaceClass } from "../../styles/field";
import { menuContentMotionClass, overlayCollision, thinScrollbarClass } from "../../styles/overlay";

export interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

export interface SelectProps {
    label?: string;
    error?: string;
    helperText?: string;
    fullWidth?: boolean;
    options: Array<{ value: string | number; label: string; disabled?: boolean }>;
    size?: "sm" | "md" | "lg";
    value?: string;
    defaultValue?: string;
    /** Prefix icon/node inside the select trigger */
    prefix?: React.ReactNode;
    /** Left icon inside the select trigger */
    leftIcon?: React.ReactNode;
    /** Prefer onValueChange. onChange keeps legacy `{ target: { value } }` shape for existing apps. */
    onChange?: (e: { target: { value: string; name?: string } }) => void;
    onValueChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    id?: string;
    name?: string;
    className?: string;
    required?: boolean;
}

export const Select: React.FC<SelectProps> = ({
    label,
    error,
    helperText,
    fullWidth = false,
    options,
    size = "md",
    value,
    defaultValue,
    prefix,
    leftIcon,
    onChange,
    onValueChange,
    placeholder = "Select…",
    disabled,
    id,
    name,
    className,
    required,
}) => {
    const selectId = id || `select-${Math.random().toString(36).slice(2, 9)}`;
    const hasError = !!error;
    const EMPTY = "__mz_empty__";
    const toRadix = (v?: string): string => (!v ? EMPTY : v);
    const fromRadix = (v: string) => (v === EMPTY ? "" : v);

    const handleChange = (v: string) => {
        const next = fromRadix(v);
        onValueChange?.(next);
        if (!onChange) return;
        (onChange as (e: { target: { value: string; name?: string } }) => void)({
            target: { value: next, name: name ?? "" },
        });
    };

    return (
        <div className={clsx("flex flex-col gap-1.5", fullWidth && "w-full", className)}>
            {label ? (
                <label htmlFor={selectId} className="text-sm font-medium text-neutral-dark dark:text-white">
                    {label}
                    {required ? <span className="ms-0.5 text-danger">*</span> : null}
                </label>
            ) : null}
            <SelectPrimitive.Root
                value={value === undefined ? undefined : toRadix(value)}
                defaultValue={defaultValue === undefined ? undefined : toRadix(defaultValue)}
                onValueChange={handleChange}
                disabled={disabled}
                name={name}
                required={required}
            >
                <SelectPrimitive.Trigger
                    id={selectId}
                    aria-invalid={hasError}
                    className={clsx(
                        fieldSurfaceClass({ error: hasError, disabled }),
                        CONTROL_HEIGHT[size],
                        "inline-flex w-full items-center justify-between gap-2 px-3 text-start",
                        "data-[placeholder]:text-neutral-secondary/70",
                    )}
                >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        {prefix ?? leftIcon}
                        <SelectPrimitive.Value placeholder={placeholder} />
                    </div>
                    <SelectPrimitive.Icon asChild>
                        <ChevronDown size={18} className="shrink-0 text-primary dark:text-gold-light" aria-hidden />
                    </SelectPrimitive.Icon>
                </SelectPrimitive.Trigger>
                <SelectPrimitive.Portal>
                    <SelectPrimitive.Content
                        position="popper"
                        sideOffset={6}
                        avoidCollisions={overlayCollision.avoidCollisions}
                        collisionPadding={overlayCollision.collisionPadding}
                        className={clsx(
                            "z-[80] overflow-hidden rounded-xl border border-neutral-border bg-white shadow-mz-lg dark:border-white/12 dark:bg-neutral-navy",
                            "min-w-[var(--radix-select-trigger-width)]",
                            menuContentMotionClass,
                        )}
                    >
                        <SelectPrimitive.Viewport className={clsx("max-h-56 p-1", thinScrollbarClass)}>
                            {options.map((opt) => (
                                <SelectPrimitive.Item
                                    key={String(opt.value)}
                                    value={toRadix(String(opt.value))}
                                    disabled={opt.disabled}
                                    className={clsx(
                                        "relative flex cursor-pointer select-none items-center rounded-lg py-2 pe-2.5 ps-8 text-sm outline-none",
                                        "data-[disabled]:pointer-events-none data-[disabled]:opacity-40",
                                        "data-[highlighted]:bg-neutral-light dark:data-[highlighted]:bg-white/10",
                                        "text-neutral-dark dark:text-white",
                                    )}
                                >
                                    <span className="absolute start-2.5 flex h-3.5 w-3.5 items-center justify-center">
                                        <SelectPrimitive.ItemIndicator>
                                            <Check size={14} className="text-primary dark:text-gold-light" />
                                        </SelectPrimitive.ItemIndicator>
                                    </span>
                                    <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
                                </SelectPrimitive.Item>
                            ))}
                        </SelectPrimitive.Viewport>
                    </SelectPrimitive.Content>
                </SelectPrimitive.Portal>
            </SelectPrimitive.Root>
            {error ? (
                <p id={`${selectId}-error`} className="text-sm text-danger" role="alert">
                    {error}
                </p>
            ) : null}
            {helperText && !error ? (
                <p id={`${selectId}-helper`} className="text-sm text-neutral-secondary">
                    {helperText}
                </p>
            ) : null}
        </div>
    );
};

Select.displayName = "Select";
